import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import RecorderLeading from "./RecorderLeading";
import RecorderTrailing from "./RecorderTrailing";
import RecorderWaveform from "./RecorderWaveform";

const formatClock = (ms: number) => {
	const totalSeconds = Math.max(0, Math.round(ms / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
		2,
		"0",
	)}`;
};

export type AudioRecorderState =
	| "default"
	| "recording"
	| "recorded"
	| "expired";

export type AudioRecorderType = "default" | "autoRecord";

export type AudioRecorderLabels = {
	default?: string;
	recording?: string;
	recorded?: string;
	expired?: string;
	autoRecordPrompt?: string;
	unavailable?: string;
};

type Props = {
	state: AudioRecorderState;
	type?: AudioRecorderType;
	handleRecord: () => void;
	handleDelete?: () => void;
	showDiscardButton?: boolean;
	allowStopWhileRecording?: boolean;
	onSeek?: (progress: number) => Promise<void>;
	disabled?: boolean;
	isLoading?: boolean;
	isPlaying?: boolean;
	meterHistory?: number[];
	progress?: number;
	currentTime?: number;
	totalTime?: number;
	remainingSeconds?: number;
	labels?: AudioRecorderLabels;
	formatTimerLabel?: (seconds: number) => string;
};

const resolveIdleLabels = (
	disabled: boolean,
	labels: AudioRecorderLabels | undefined,
	t: TFunction,
) => {
	if (disabled) {
		const unavailableLabel =
			labels?.unavailable ?? t("recorder.responseAudioUnavailable");
		return {
			defaultLabel: unavailableLabel,
			autoRecordPromptLabel: unavailableLabel,
		};
	}
	return {
		defaultLabel: labels?.default ?? t("recorder.tapToRecord"),
		autoRecordPromptLabel: labels?.autoRecordPrompt ?? t("recorder.getReady"),
	};
};

const AudioRecorder = ({
	state,
	type = "default",
	handleRecord,
	handleDelete,
	showDiscardButton = true,
	allowStopWhileRecording,
	onSeek,
	disabled = false,
	isLoading = false,
	isPlaying = false,
	meterHistory = [],
	progress = 0,
	currentTime,
	totalTime,
	remainingSeconds = 0,
	labels,
	formatTimerLabel,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const isRecording = state === "recording";
	const isRecorded = state === "recorded";
	const showWaveform = isRecording || isRecorded;

	const { defaultLabel, autoRecordPromptLabel } = resolveIdleLabels(
		disabled,
		labels,
		t,
	);
	const recordingLabel = labels?.recording ?? t("recorder.recording");
	const recordedLabel = labels?.recorded ?? t("recorder.recordingCompleted");
	const expiredLabel = labels?.expired ?? t("recorder.timeIsUp");

	const progressText = `${formatClock(currentTime ?? 0)}/${formatClock(
		totalTime ?? 0,
	)}`;

	const recordingProgress =
		totalTime && totalTime > 0
			? Math.min(1, (currentTime ?? 0) / totalTime)
			: 0;

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			width="100%"
			gap={16}
			paddingHorizontal={16}
			paddingVertical={12}
			minHeight={72}
			borderRadius={theme.roundness * 2}
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			backgroundColor={theme.colors.surface}
		>
			<RecorderLeading
				state={state}
				type={type}
				isPlaying={isPlaying}
				isLoading={isLoading}
				disabled={disabled}
				defaultLabel={defaultLabel}
				recordingLabel={recordingLabel}
				recordedLabel={recordedLabel}
				expiredLabel={expiredLabel}
				autoRecordPromptLabel={autoRecordPromptLabel}
				handleRecord={handleRecord}
			/>
			{showWaveform && (
				<RecorderWaveform
					isRecording={isRecording}
					isRecorded={isRecorded}
					progress={isRecording ? recordingProgress : progress}
					meterHistory={meterHistory}
					onSeek={onSeek}
				/>
			)}
			<RecorderTrailing
				state={state}
				type={type}
				progressText={progressText}
				remainingSeconds={remainingSeconds}
				formatTimerLabel={formatTimerLabel}
				showDiscardButton={showDiscardButton}
				allowStopWhileRecording={allowStopWhileRecording}
				isLoading={isLoading}
				disabled={disabled}
				handleRecord={handleRecord}
				handleDelete={handleDelete}
			/>
		</Box>
	);
};

export default AudioRecorder;
